import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useMechanicShare } from '@/contexts/MechanicShareContext';
import { Car, CarIssue } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import MediaUploader from '@/components/MediaUploader';
import { toast } from 'sonner';

interface MechanicRepairFormProps {
  token: string;
  car: Car;
  onSuccess: () => void;
}

interface FormValues {
  title: string;
  description: string;
  date: string;
  cost: string;
  mechanicName: string;
  garageName: string;
  resolvedIssues: string[];
}

const MechanicRepairForm: React.FC<MechanicRepairFormProps> = ({ token, car, onSuccess }) => {
  const { logMechanicRepair } = useMechanicShare();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  
  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      cost: '',
      mechanicName: '',
      garageName: '',
      resolvedIssues: [],
    }
  });

  // Only show open issues
  const openIssues = car.issues?.filter(issue => 
    issue.status === 'new' || issue.status === 'partially_resolved'
  ) || [];

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    
    try {
      // Split resolved issues to fully resolved and partially resolved
      const resolvedIssues: string[] = [];
      const partiallyResolvedIssues: string[] = [];
      
      values.resolvedIssues.forEach(value => {
        if (value.startsWith('partial-')) {
          partiallyResolvedIssues.push(value.replace('partial-', ''));
        } else {
          resolvedIssues.push(value);
        }
      });
      
      const repairId = logMechanicRepair(token, {
        title: values.title,
        description: values.description,
        date: values.date,
        cost: values.cost ? parseFloat(values.cost) : undefined,
        mechanicName: values.mechanicName,
        garageName: values.garageName || undefined,
        receiptImage: uploadedImage || undefined,
        resolvedIssues,
        partiallyResolvedIssues,
      });
      
      if (repairId) {
        toast.success('Repair record added successfully');
        onSuccess();
      } else {
        toast.error('Failed to add repair record');
      }
    } catch (error) {
      console.error('Error adding repair record:', error);
      toast.error('An error occurred while saving');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (urls: string[]) => {
    if (urls.length > 0) {
      setUploadedImage(urls[0]);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          rules={{ required: 'Title is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Repair Title*</FormLabel>
              <FormControl>
                <Input placeholder="Oil Change, Brake Replacement, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          rules={{ required: 'Description is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description*</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe the repair that was performed..." 
                  {...field} 
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="date"
            rules={{ required: 'Date is required' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date*</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="0.00" 
                    step="0.01" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="mechanicName"
          rules={{ required: 'Your name is required' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your Name*</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="garageName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Garage/Shop Name</FormLabel>
              <FormControl>
                <Input placeholder="ABC Auto Repair" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="border rounded-md p-3">
          <FormLabel className="block mb-2">Receipt/Image (Optional)</FormLabel>
          <MediaUploader
            onUpload={handleImageUpload}
            maxFiles={1}
            allowVideo={false}
            showPreview
          />
        </div>

        {openIssues.length > 0 && (
          <div className="border rounded-md p-3">
            <FormLabel className="block mb-2">Which issues did this repair address?</FormLabel>
            
            {openIssues.map((issue) => (
              <div key={issue.id} className="space-y-2 pt-2 first:pt-0">
                <h4 className="font-medium text-sm">{issue.title}</h4>
                <div className="space-y-1">
                  <FormField
                    control={form.control}
                    name="resolvedIssues"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={issue.id}
                          checked={field.value?.includes(issue.id)}
                          onCheckedChange={(checked) => {
                            return checked
                              ? field.onChange([...field.value, issue.id])
                              : field.onChange(
                                  field.value?.filter(
                                    (value) => value !== issue.id && value !== `partial-${issue.id}`
                                  )
                                )
                          }}
                        />
                        <label
                          htmlFor={issue.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Fully Resolved
                        </label>
                      </div>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="resolvedIssues"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`partial-${issue.id}`}
                          checked={field.value?.includes(`partial-${issue.id}`)}
                          onCheckedChange={(checked) => {
                            const partialKey = `partial-${issue.id}`;
                            return checked
                              ? field.onChange([...field.value, partialKey])
                              : field.onChange(
                                  field.value?.filter((value) => value !== partialKey)
                                )
                          }}
                        />
                        <label
                          htmlFor={`partial-${issue.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Partially Resolved
                        </label>
                      </div>
                    )}
                  />
                </div>
                <p className="text-xs text-gray-500 line-clamp-2">{issue.description}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-lime hover:bg-lime/90 text-black"
          >
            {isLoading ? 'Saving...' : 'Save Repair Record'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default MechanicRepairForm;
